/**
 * @file governanceService.ts
 * @description Frontend service and React Query hooks for Governance Dashboard
 * @feature 142-governance-compliance-dashboard
 * @constitution v1.12.0
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../apiClient';
import type {
  TenantSettingsSummary,
  TenantSetting,
  WorkspaceAccessEntry,
  WorkspaceAccessSummary,
  Pagination,
  SharedArtifactsSummary,
  SharedArtifact,
  DatasourcesSummary,
  DatasourceEntry,
  GatewaysSummary,
  Gateway,
  GatewayDatasource,
} from '../../types/governance';

const API_BASE = '/api/operations/governance';

// ============================================================================
// API Fetch Helper
// ============================================================================

async function fetchApi<T>(endpoint: string): Promise<T> {
  const response = await apiClient.get<{ success: boolean; data: T; error?: string }>(
    `${API_BASE}${endpoint}`
  );
  if (!response.data.success) {
    throw new Error(response.data.error || 'API returned unsuccessful response');
  }
  return response.data.data;
}

// ============================================================================
// T019: useTenantSettings (US1)
// ============================================================================

interface TenantSettingsData {
  summary: TenantSettingsSummary;
  settings: TenantSetting[];
  categories: string[];
}

export function useTenantSettings() {
  return useQuery<TenantSettingsData>({
    queryKey: ['governance', 'tenant-settings'],
    queryFn: () => fetchApi<TenantSettingsData>('/tenant-settings'),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

// ============================================================================
// T026: useWorkspaceAccess + useWorkspaceAccessSummary (US2)
// ============================================================================

interface WorkspaceAccessData {
  entries: WorkspaceAccessEntry[];
  pagination: Pagination;
}

export function useWorkspaceAccess(params?: {
  workspace?: string;
  principal?: string;
  role?: string;
  principalType?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  const queryString = params
    ? '?' + new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v != null && v !== '')
          .map(([k, v]) => [k, String(v)])
      ).toString()
    : '';

  return useQuery<WorkspaceAccessData>({
    queryKey: ['governance', 'workspace-access', params],
    queryFn: () => fetchApi<WorkspaceAccessData>(`/workspace-access${queryString}`),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useWorkspaceAccessSummary() {
  return useQuery<WorkspaceAccessSummary>({
    queryKey: ['governance', 'workspace-access', 'summary'],
    queryFn: () => fetchApi<WorkspaceAccessSummary>('/workspace-access/summary'),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

// ============================================================================
// T032: useSharedArtifacts (US3)
// ============================================================================

interface SharedArtifactsData {
  summary: SharedArtifactsSummary;
  artifacts: SharedArtifact[];
}

export function useSharedArtifacts() {
  return useQuery<SharedArtifactsData>({
    queryKey: ['governance', 'shared-artifacts'],
    queryFn: () => fetchApi<SharedArtifactsData>('/shared-artifacts'),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

// ============================================================================
// T038: useDatasources (US4)
// ============================================================================

interface DatasourcesData {
  summary: DatasourcesSummary;
  datasources: DatasourceEntry[];
}

export function useDatasources() {
  return useQuery<DatasourcesData>({
    queryKey: ['governance', 'datasources'],
    queryFn: () => fetchApi<DatasourcesData>('/datasources'),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

// ============================================================================
// T044: useGateways + useGatewayDatasources (US5)
// ============================================================================

interface GatewaysData {
  summary: GatewaysSummary;
  gateways: Gateway[];
}

export function useGateways() {
  return useQuery<GatewaysData>({
    queryKey: ['governance', 'gateways'],
    queryFn: () => fetchApi<GatewaysData>('/gateways'),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useGatewayDatasources(gatewayId: string | null) {
  return useQuery<GatewayDatasource[]>({
    queryKey: ['governance', 'gateways', gatewayId, 'datasources'],
    queryFn: () =>
      fetchApi<GatewayDatasource[]>(`/gateways/${encodeURIComponent(gatewayId!)}/datasources`),
    enabled: !!gatewayId,
    staleTime: 30 * 1000,
  });
}

// ============================================================================
// CSV Export Utility
// ============================================================================

export function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
